import ChipSolid from "@/toggle-ui-kit/chip/chip-solid";
import { PrevNextControl } from "@/toggle-ui-kit/prev-next-control";
import {
  type DsdEditorSumValidationCell,
  formatDsdEditorSumDifference,
  isDsdEditorSumMismatch,
} from "@/utils/dsd-editor/sum-validation";
import { Sigma, TriangleAlert } from "lucide-react";
import type { ReactNode } from "react";

// 상태 칩 (Figma Chip / Text / Solid 24px). 검은 경고 = 합계 불일치·단수 차이, 주황 시그마 = 합계
const SumStatusChip = ({
  mismatch,
  roundingDiff,
}: {
  mismatch: boolean;
  roundingDiff: boolean;
}) => (
  <ChipSolid
    size="24px"
    rounded={false}
    showRightIcon={false}
    leftIcon={
      mismatch ? (
        <TriangleAlert className="size-4" />
      ) : (
        <Sigma className="size-4" />
      )
    }
    className={
      mismatch
        ? "bg-primary-base text-static-white"
        : "bg-orange-500 text-static-white"
    }
  >
    {mismatch ? (roundingDiff ? "단수 차이" : "합계 불일치") : "합계"}
  </ChipSolid>
);

/** 라벨은 상자 위, 값만 상자 안 (Figma 7647-489038) */
const ValueField = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-col gap-1">
    <p className="label-2-medium text-label-alternative">{label}</p>
    <p className="truncate rounded-[8px] bg-background-neutral-alternative px-3 py-4 body-2-normal-medium text-label-normal">
      {value || "-"}
    </p>
  </div>
);

/**
 * 선택한 합계 셀의 상세 (Figma 7647-488897). 세 묶음을 24px 간격으로:
 * 선택 셀 요약 + 경고 / 상태 칩·이전다음 + 입력한 값·계산된 합 / (단수 차이가 있을 때만) 구분선 + 단수 차이
 */
export const DsdReviewSumValidationDetail = ({
  summary,
  cell,
  cells,
  onPrevious,
  onNext,
}: {
  /** 맨 위 선택 셀 카드 — 경고와 한 묶음(8px)으로 붙는다 */
  summary: ReactNode;
  cell: DsdEditorSumValidationCell;
  /** 이전/다음이 도는 셀 (불일치 셀) */
  cells: DsdEditorSumValidationCell[];
  onPrevious: () => void;
  onNext: () => void;
}) => {
  const difference = formatDsdEditorSumDifference(cell.difference);
  const mismatch = isDsdEditorSumMismatch(cell);
  const currentIndex = cells.findIndex((item) => item.cellId === cell.cellId);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        {summary}
        {mismatch && (
          <p className="flex items-center gap-1 rounded-[8px] bg-rose-100 p-2 text-red-600">
            <TriangleAlert className="size-[18px] shrink-0" />
            <span className="truncate label-2-regular">
              {difference
                ? "단수 차이가 발생했습니다. 합계를 확인해 주세요."
                : "합계가 일치하지 않습니다."}
            </span>
          </p>
        )}
      </div>
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-2">
          <SumStatusChip mismatch={mismatch} roundingDiff={!!difference} />
          {mismatch && (
            <PrevNextControl
              index={currentIndex}
              total={cells.length}
              prevLabel="이전 합계 셀"
              nextLabel="다음 합계 셀"
              onPrev={onPrevious}
              onNext={onNext}
            />
          )}
        </div>
        <ValueField label="입력한 값" value={cell.value} />
        {/* 합계 반영 버튼은 이 상자 오른쪽에 들어갈 자리 (아직 없음) */}
        <ValueField label="계산된 합" value={cell.calculatedValue ?? "-"} />
      </div>
      {difference && (
        <div className="flex flex-col gap-4">
          <hr className="border-line-normal" />
          <ValueField label="단수 차이" value={difference} />
        </div>
      )}
    </div>
  );
};
